import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ConfigurationService } from 'src/app/core/config/configuration.service';
import { HttpParamsFromListRequestDTO, ListRequestDTO, ListResponseDTO } from '..';
import { ChairOrderStatus, WaterSupplies } from './chairs.proxy';

@Injectable()
export class ChairOrdersProxy {
    constructor(
        private http: HttpClient,
        private configurationService: ConfigurationService,
    ) {}

    async getChairOrders(
        options: ListRequestDTO & {
            groupId?: string;
            practiceId?: string;
            status?: ChairOrderStatus;
        },
    ): Promise<ListResponseDTO<ChairOrderSummaryDTO>> {
        const url = this.configurationService.APIURL + '/api/chair-orders';
        let params = HttpParamsFromListRequestDTO(options);
        if (options.groupId) {
            params = params.append('groupId', String(options.groupId));
        }
        if (options.practiceId) {
            params = params.append('practiceId', String(options.practiceId));
        }
        if (options.status) {
            params = params.append('status', String(options.status));
        }

        return this.http
            .get<ListResponseDTO<ChairOrderSummaryDTO>>(url, { params: params })
            .pipe(
                tap((r) =>
                    r.items.forEach((orderDetails) => {
                        const canDelete = orderDetails.status <= ChairOrderStatus.InLab;
                        orderDetails.canDelete = canDelete;
                    }),
                ),
            )
            .toPromise();
    }

    async getChairOrdersPerPractice(
        options: ListRequestDTO & { status?: ChairOrderStatus },
    ): Promise<ListResponseDTO<ChairOrdersForPracticeDT0>> {
        const url = this.configurationService.APIURL + '/api/chair-orders/per-practice';
        let params = HttpParamsFromListRequestDTO(options);
        if (options.status) {
            params = params.append('status', String(options.status));
        }
        return this.http.get<ListResponseDTO<ChairOrdersForPracticeDT0>>(url, { params: params }).toPromise();
    }

    async getChairOrderDetails(groupId: string, practiceId: string, chairOrderId: string): Promise<ChairOrderDetailsDTO> {
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        if (practiceId) {
            params = params.append('practiceId', String(practiceId));
        }
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}`;
        return await this.http.get<ChairOrderDetailsDTO>(url, { params }).toPromise();
    }

    async getReport(groupId: string, practiceId: string, chairOrderId: string): Promise<Blob> {
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}/report`;
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        if (practiceId) {
            params = params.append('practiceId', String(practiceId));
        }
        return await this.http.get(url, { responseType: 'blob', params: params }).toPromise();
    }

    async getAttachment(groupId: string, practiceId: string, chairOrderId: string): Promise<Blob> {
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}/attachment`;
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        if (practiceId) {
            params = params.append('practiceId', String(practiceId));
        }
        return await this.http.get(url, { responseType: 'blob', params: params }).toPromise();
    }

    async createChairOrder(groupId: string, practiceId: string, request: CreateChairOrderRequest): Promise<object> {
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        params = params.append('practiceId', String(practiceId));
        const url = this.configurationService.APIURL + '/api/chair-orders';
        return await this.http.post(url, request, { params }).toPromise();
    }

    async deleteChairOrder(practiceId: string, orderChairOrderId: string): Promise<any> {
        const url = `${this.configurationService.APIURL}/api/chair-orders/${orderChairOrderId}`;
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return await this.http.delete(url, { params: params }).toPromise();
    }

    async createActionItem(groupId: string, practiceId: string, chairOrderId: string, actionItemText: string): Promise<object> {
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        if (practiceId) {
            params = params.append('practiceId', String(practiceId));
        }
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}/action-items`;
        return await this.http.post(url, { text: actionItemText }, { params }).toPromise();
    }

    async getActionItem(groupId: string, practiceId: string, chairOrderId: string, actionItemId: string): Promise<ActionItemDTO> {
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        if (practiceId) {
            params = params.append('practiceId', String(practiceId));
        }
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}/action-items/${actionItemId}`;
        return await this.http.get<ActionItemDTO>(url, { params }).toPromise();
    }

    async checkActionItem(
        groupId: string,
        practiceId: string,
        chairOrderId: string,
        actionItemId: string,
        request: CheckActionItemDTO,
    ): Promise<object> {
        let params = new HttpParams();
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        if (practiceId) {
            params = params.append('practiceId', String(practiceId));
        }
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}/action-items/${actionItemId}/check`;
        return await this.http.post(url, request, { params }).toPromise();
    }

    async updateRetestStatus(groupId: string, practiceId: string, chairOrderId: string, isApproved: boolean): Promise<object> {
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        if (groupId) {
            params = params.append('groupId', String(groupId));
        }
        const url = `${this.configurationService.APIURL}/api/chair-orders/${chairOrderId}/retest-status`;
        return await this.http.post(url, { isApproved: isApproved }, { params }).toPromise();
    }

    async sendKitsToPractice(sendKitsToPracticeDTO: SendKitsToPracticeDTO): Promise<BlobWrapper> {
        const url = this.configurationService.APIURL + '/api/chair-orders/send-kits';
        return await this.postAndDownloadBlob(url, sendKitsToPracticeDTO);
    }

    async startLabProcessing(startLabProcessingDTO: StartLabProcessingDTO): Promise<BlobWrapper> {
        const url = this.configurationService.APIURL + '/api/chair-orders/labProcessing';
        return await this.postAndDownloadBlob(url, startLabProcessingDTO);
    }

    async getQualityReport2(groupId: string, year: number): Promise<BlobWrapper> {
        const url = this.configurationService.APIURL + '/api/chair-orders/quality-report2';
        let params = new HttpParams();
        params = params.append('groupId', String(groupId));
        params = params.append('year', String(year));
        return await this.postAndDownloadBlob(url, {}, params);
    }

    async getGroupQuality(groupId: string, from?: Date, to?: Date): Promise<GroupQualityDTO> {
        let params = new HttpParams();
        params = params.append('groupId', String(groupId));
        if (from) {
            params = params.append('from', this.toLocalDateString(from));
        }
        if (to) {
            params = params.append('to', this.toLocalDateString(to));
        }
        const url = `${this.configurationService.APIURL}/api/chair-orders/group-quality`;
        return await this.http.get<GroupQualityDTO>(url, { params }).toPromise();
    }

    private toLocalDateString(date: Date): string {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    createReport(practiceId: string, formData: FormData): Observable<any> {
        const url = this.configurationService.APIURL + '/api/chair-orders/report';
        let params = new HttpParams();
        params = params.append('practiceId', String(practiceId));
        return this.http.post(url, formData, {
            reportProgress: true,
            observe: 'events',
            params: params,
        });
    }

    private async postAndDownloadBlob(url: string, body: any, params?: HttpParams): Promise<BlobWrapper> {
        const response = await this.http.post(url, body, { observe: 'response', responseType: 'blob' as 'json', params }).toPromise();
        const blobResponse = <HttpResponse<Blob>>response;
        const contentDisposition = blobResponse.headers.get('content-disposition');
        const filename = contentDisposition.split(';')[1].split('filename')[1].split('=')[1].trim();
        return <BlobWrapper>{
            blob: blobResponse.body,
            filename: filename,
        };
    }

    async getQualityChairOrderPractices(groupId: string): Promise<QualityPracticeDTO[]> {
        let url = this.configurationService.APIURL + '/api/chair-orders/practices2';
        let params = new HttpParams();
        params = params.append('groupId', String(groupId));
        return await this.http.get<QualityPracticeDTO[]>(url, { params }).toPromise();
    }
}

export interface ChairOrdersForPracticeDT0 {
    practiceId: string;
    practiceName: string;
    groupId: string | null;
    chairOrders: ChairOrderSummaryDTO[];
}

export interface ChairOrderSummaryDTO {
    id: string;
    labNumber: string;
    status: ChairOrderStatus;
    practiceId: string;
    practiceName: string;

    chairId: string;
    chairName: string;
    chairBrand: string;
    chairWaterSupply: WaterSupplies;

    nbOpenActionItems: number;
    actionItems: { id: string; text: string }[];
    result: number | null;

    createdOn: Date | string;
    toBeExecutedOn: Date | string;
    kitSentOn: Date | string | null;
    sampledOn: Date | string | null;
    processedOn: Date | string | null;
    resultOutOn: Date | string | null;

    report: {
        createdOn: string;
        isRead: boolean;
        fileName: string;
        message: string;
    };

    // client-side added
    canDelete?: boolean;
}

export interface ChairOrderDetailsDTO {
    id: string;
    createdOn: Date | string;
    labNumber: string;
    status: ChairOrderStatus;
    practiceId: string;
    practiceName: string;
    practiceLanguage: string;
    groupId: string;
    chairId: string;
    chairName: string;
    chairBrand: string;

    toBeExecutedOn: Date | string;
    kitSentOn: Date | string | null;
    sampledOn: Date | string | null;
    inLabReceivedOn: Date | string | null;
    processedOn: Date | string | null;
    resultOutOn: Date | string | null;

    result: number | null;
    resultMessage: string;
    actionItems: {
        id: string;
        text: string;
        isChecked: boolean;
        checkedBy: string | null;
        checkedOn: Date | string | null;
        remarks: string;
    }[];

    report: {
        createdOn: string;
        isRead: boolean;
        fileName: string;
        assignees: string[];
        message: string;
    };
    attachmentFileName: string;

    test: {
        reference: string;
        name: string;
        primaryColorHex: string;
        secondaryColorHex: string;
    };

    retestFor: {
        id: string;
        labNumber: string;
        result: number;
        resultOutOn: Date | string | null;
    };
}

export interface ActionItemDTO {
    id: string;
    chairOrderId: string;
    chairOrderLabNumber: string;
    chairId: string;
    chairName: string;
    isChecked: boolean;
    key: ActionItemKeys;
    text: string;
    checkedRemarks: string;
}

export interface CheckActionItemDTO {
    remarks: string;
}

export interface CreateChairOrderRequest {
    chairId: string;
}

interface BlobWrapper {
    blob: Blob;
    filename: string;
}

export interface SendKitsToPracticeDTO {
    chairOrderIds: string[];
    practiceIds: string[];
}

export interface StartLabProcessingDTO {
    chairOrderIds: string[];
    practiceIds: string[];
}

export interface CreateChairOrdersReportDTO {
    practiceId: string;
    practiceName: string;
    chairOrders: {
        id: string;
        cFUValue: number | null;
        resultMessage: string;
        actionItems: { id: string | null; text: string }[];
        shouldRetest: boolean;
    }[];
    message: string;
    assignees: string[];
}

export enum ActionItemKeys {
    One,
    Two,
    Three,
    Custom,
}

export interface GroupQualityDTO {
    groupName: string;
    nbPractices: number;
    nbChairs: number;
    nbOpenChairOrders: number;
    nbOpenActionItems: number;
    nbGreen: number;
    nbYellow: number;
    nbOrange: number;
    nbRed: number;
}

export interface QualityPracticeDTO {
    id: string;
    name: string;
    debtorNumber: string;
    nbChairs: number;
    nbResultOut: number;
    nbOpenActionItems: number;
    nbGreen: number;
    nbYellow: number;
    nbOrange: number;
    nbRed: number;
}
